import { RECUR_DEFAULT_HOURS } from 'app/area/company-report/constants';
import { ScheduleDefinition } from 'app/area/company-report/types-store';
import { codeItemToStoreValue, storeValueToCodeItem } from 'app/infrastructure/data/codes';
import produce from 'immer';
import { ComplexValue } from 'ui/common/types';

export const toUTCDateWithLocalOffset = (value: Date): Date => 
  value && new Date(value.getTime() - value.getTimezoneOffset() * 60000);

export const fromUTCDateWithLocalOffset = (value: Date): Date => 
  value && new Date(value.getTime() + value.getTimezoneOffset() * 60000);

const joinTimeAndDate = (timePart: Date, datePart: Date): Date => {
    return timePart && datePart && new Date(
      datePart.getFullYear(),
      datePart.getMonth(),
      datePart.getDate(),
      timePart.getHours(),
      timePart.getMinutes(),
      timePart.getSeconds()
    );
  };

export const isValueEmpty = (value: number | ComplexValue<number>): boolean =>
  value === undefined || (value as ComplexValue<number>)?.value  === 0 || !String(value).length;

export const onChangeCreator = (
  transformDefinition: (val: any) => (state: ScheduleDefinition) => void,
  setScheduleDefinition: React.Dispatch<React.SetStateAction<ScheduleDefinition>>
  ) => {
  return (val: any) => {
    setScheduleDefinition(prevState => 
      produce(prevState, transformDefinition(val))
    );
  };
};

export const updateWithRangeOfRecurrenceOption = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurrenceRange = val;
};

const isValidDate = (input: Date): boolean => !!input && !isNaN(input.getTime());

export const updateWithExecutionTime = (val: Date) => (state: ScheduleDefinition) => {
  const execTime = fromUTCDateWithLocalOffset(val);
  const recurRangeStartDatePart = !!state.recurRangeStart ? new Date(state.recurRangeStart) : new Date();
  const joined = joinTimeAndDate(execTime, recurRangeStartDatePart);
  state.recurRangeStart = isValidDate(joined) ? joined?.toISOString() : undefined;
};

export const updateWithRangeStartDay = (val: Date) => (state: ScheduleDefinition) => {
  const startAt = fromUTCDateWithLocalOffset(val);
  const recurRangeStartTimePart = !!state.recurRangeStart ? new Date(state.recurRangeStart) : new Date();
  const joined = joinTimeAndDate(recurRangeStartTimePart, startAt);
  state.recurRangeStart = isValidDate(joined) ? joined?.toISOString() : undefined;
};

export const updateWithRangeEndByDate = (val: Date) => (state: ScheduleDefinition) => {
  state.recurRangeEndBy = val && new Date(fromUTCDateWithLocalOffset(val)?.setHours(...RECUR_DEFAULT_HOURS))?.toISOString();
};

export const updateWithRangeEndAfterOccurences = (val: number) => (state: ScheduleDefinition) => {
  state.recurRangeEndAfter = val;
};

export const updateWithDailyDaysInterval = (val: number)  => (state: ScheduleDefinition) =>
{
  state.recurDaily.daysInterval = val;
};

export const updateWithDailyOption = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurDaily.option = val;
};

export const updateWithAmountOfWeeks = (val: number) => (state: ScheduleDefinition) => {
  state.recurWeekly.weeksInterval = val;
};

export const updateWithWeekday = (val: ComplexValue<number>[]) => (state: ScheduleDefinition) =>
{
  state.recurWeekly.daysOfWeek = val;
};

export const updateWithDayOfMonth = (val: number) => (state: ScheduleDefinition) => {
  state.recurMonthly.dayOfMonth = val;
};

export const updateWithMonthlyOption = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurMonthly.option = val;
};

export const updateWithMonthlyInterval = (val: number) => (state: ScheduleDefinition) => {
  state.recurMonthly.monthsInterval = val;
};

export const updateWithMonthlyOccurence = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurMonthly.occurrence = val;
};

export const updateWithMonthlyDayOfWeek = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurMonthly.dayOfWeek = val;
};

export const updateWithYearsInterval = (val: number) => (state: ScheduleDefinition) => {
  state.recurYearly.yearsInterval = val;
};

export const updateWithYearlyOption = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurYearly.option = val;
};

export const updateWithYearlyOnDayMonth = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurYearly.month1 = val;
};

export const updateWithYearlyOnWeekdayMonth = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurYearly.month2 = val;
};

export const updateWithYearlyDayOfMonth = (val: number) => (state: ScheduleDefinition) => {
  state.recurYearly.dayOfMonth = val;
};

export const updateWithYearlyDayOfWeek = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurYearly.dayOfWeek = val;
};

export const updateWithYearlyOccurence = (val: ComplexValue<number>) => (state: ScheduleDefinition) => {
  state.recurYearly.occurrence= val;
};

export const mapScheduleDefinitionToWire = (definition: ScheduleDefinition) => {
 
  if(!definition) {
    return undefined;
  }
  
  return [{
    ...definition,
    recurrencePattern: storeValueToCodeItem(definition.recurrencePattern),
    recurrenceRange: storeValueToCodeItem(definition.recurrenceRange),
    recurDaily: {
      ...definition.recurDaily,
      option: storeValueToCodeItem(definition.recurDaily.option)
    },
    recurWeekly: {
      ...definition.recurWeekly,
      daysOfWeek: definition.recurWeekly.daysOfWeek?.map((d: any) => ({value: d.value, description: d.title}))
        .map(code => ({dayOfWeek: storeValueToCodeItem(code)}))
    },
    recurMonthly: {
      ...definition.recurMonthly,
      option: storeValueToCodeItem(definition.recurMonthly.option),
      occurrence: storeValueToCodeItem(definition.recurMonthly.occurrence),
      dayOfWeek: storeValueToCodeItem(definition.recurMonthly.dayOfWeek)
    },
    recurYearly: {
      ...definition.recurYearly,
      option: storeValueToCodeItem(definition.recurYearly.option),
      occurrence: storeValueToCodeItem(definition.recurYearly.occurrence),
      dayOfWeek: storeValueToCodeItem(definition.recurYearly.dayOfWeek),
      month1: storeValueToCodeItem(definition.recurYearly.month1),
      month2: storeValueToCodeItem(definition.recurYearly.month2)
    }
  }];
};

export const mapWireToScheduleDefinition = (wireDefinition: any) => {

  if(!wireDefinition) {
    return undefined;
  }

  const currentDatePlus170DaysISOString = new Date(new Date(Date.now() + (170 * 3600 * 1000 * 24))
    .setHours(...RECUR_DEFAULT_HOURS)).toISOString();

  return {
    ...wireDefinition,
    timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
    recurrencePattern: codeItemToStoreValue(wireDefinition.recurrencePattern),
    recurrenceRange: codeItemToStoreValue(wireDefinition.recurrenceRange),
    recurRangeStart: wireDefinition.recurRangeStart || new Date(),
    recurRangeEndBy: wireDefinition.recurRangeEndBy || currentDatePlus170DaysISOString,
    recurDaily: {
      ...wireDefinition.recurDaily,
      option: codeItemToStoreValue(wireDefinition.recurDaily.option)
    },
    recurWeekly: {
      ...wireDefinition.recurWeekly,
      daysOfWeek: wireDefinition.recurWeekly.daysOfWeek?.map((code) => codeItemToStoreValue(code.dayOfWeek))
    },
    recurMonthly: {
      ...wireDefinition.recurMonthly,
      option: codeItemToStoreValue(wireDefinition.recurMonthly.option),
      occurrence: codeItemToStoreValue(wireDefinition.recurMonthly.occurrence),
      dayOfWeek: codeItemToStoreValue(wireDefinition.recurMonthly.dayOfWeek)
    },
    recurYearly: {
      ...wireDefinition.recurYearly,
      option: codeItemToStoreValue(wireDefinition.recurYearly.option),
      occurrence: codeItemToStoreValue(wireDefinition.recurYearly.occurrence),
      dayOfWeek: codeItemToStoreValue(wireDefinition.recurYearly.dayOfWeek),
      month1: codeItemToStoreValue(wireDefinition.recurYearly.month1),
      month2: codeItemToStoreValue(wireDefinition.recurYearly.month2)
    }
  };
};

export const newDateOrUndefined = (val: string) => !!val ? new Date(val) : undefined;
